import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ApiProviderSpotService } from '../../../../../core/services/api/provider-parking/api-provider-spot.service';
import { ProviderSpot } from '../../../../../core/models/provider-parking/provider-spot.model';
import { PssLoadingComponent } from '../../../../../tools/template/pss/pss-loading/pss-loading.component';
import { ModalProviderSpotComponent } from '../../../modals/provider-parking/modal-provider-spot/modal-provider-spot.component';
import { ApiTableService } from '../../../../../core/services/api-table.service';

@Component({
  selector: 'app-provider-spot',
  standalone: true,
  imports: [CommonModule, FormsModule, PssLoadingComponent, ModalProviderSpotComponent],
  templateUrl: './provider-spot.component.html',
  styleUrl: './provider-spot.component.css'
})
export class ProviderSpotComponent implements OnInit {
  public providerSpots: ProviderSpot[] = [];
  public filteredSpots: ProviderSpot[] = [];
  public isLoading: boolean = false;

  // Management State
  public isModalOpen: boolean = false;
  public selectedSpot: ProviderSpot | null = null;

  // Pagination State
  public currentPage: number = 1;
  public pageSize: number = 10;
  public totalItems: number = 0;
  public pageSizeOptions: number[] = [10, 20, 50, 100];

  // Search & Filter State
  public searchText: string = '';
  public isFilterDropdownOpen: boolean = false;
  public filterSite: string = '';
  public siteOptions: string[] = [];

  // Sorting State
  public sortColumn: string = '';
  public sortDirection: 'asc' | 'desc' = 'asc';

  // Dashboard Stats
  public stats = [
    { title: 'จุดให้บริการทั้งหมด', value: 0, icon: 'hub', color: 'text-indigo-600 bg-indigo-50' },
    { title: 'พร้อมใช้งาน (READY)', value: 0, icon: 'check_circle', color: 'text-emerald-600 bg-emerald-50' },
    { title: 'มีปัญหา (ERROR)', value: 0, icon: 'error', color: 'text-rose-600 bg-rose-50' },
    { title: 'โครงการที่ให้บริการ', value: 0, icon: 'place', color: 'text-amber-600 bg-amber-50' }
  ];

  constructor(
    private apiProviderSpotService: ApiProviderSpotService,
    private apiTableService: ApiTableService
  ) { }

  ngOnInit(): void {
    this.getProviderSpots();
  }

  public getProviderSpots(): void {
    this.isLoading = true;
    this.apiProviderSpotService.getProviderSpots().subscribe({
      next: (res) => {
        this.providerSpots = res || [];
        if (this.providerSpots.length === 0) this.loadMockData();
        this.siteOptions = Array.from(new Set(this.providerSpots.map(s => s.site))).filter(s => s).sort();
        this.applyFilters();
        this.calculateStats();
        this.isLoading = false;
      },
      error: (err) => {
        console.error('API error:', err);
        this.loadMockData();
        this.siteOptions = Array.from(new Set(this.providerSpots.map(s => s.site))).filter(s => s).sort();
        this.applyFilters();
        this.calculateStats();
        this.isLoading = false;
      }
    });
  }

  private loadMockData(): void {
    this.providerSpots = [
      { id: 1, name: 'Entry 1', description: 'ทางเข้า 1', posId: 'POS-01', ip: '192.168.100.11', spotType: 'ทางเข้า', area: 'อาคาร A', vehicleType: 'รถยนต์', apiUser: '', apiPass: '', connectDate: '04/07/2026 08:34:43', monitor: 'ปกติ,ปกติ', status: '', deviceKey: '', defaultRate: '99999', prefix: '', sapName: '', sapNameM: '', site: 'The Market', checkCode: '' },
      { id: 2, name: 'Entry 2', description: 'ทางเข้า 2', posId: 'POS-02', ip: '192.168.100.12', spotType: 'ทางเข้า', area: 'อาคาร A', vehicleType: 'รถยนต์', apiUser: '', apiPass: '', connectDate: '04/07/2026 09:12:20', monitor: 'ผิดพลาด,ปกติ', status: '', deviceKey: '', defaultRate: '99999', prefix: '', sapName: '', sapNameM: '', site: 'The Market', checkCode: '' },
      { id: 5, name: 'Exit 1', description: 'ทางออก 1', posId: 'POS-05', ip: '192.168.100.15', spotType: 'ทางออก', area: 'อาคาร A', vehicleType: 'รถยนต์', apiUser: '', apiPass: '', connectDate: '04/07/2026 08:39:38', monitor: 'ผิดพลาด,ผิดพลาด', status: '', deviceKey: '', defaultRate: '00000', prefix: '', sapName: '', sapNameM: '', site: 'The Market', checkCode: '' },
      { id: 18, name: 'Around Entry 1', description: 'Around ทางเข้า 1', posId: 'AD-01', ip: '10.0.0.18', spotType: 'ทางเข้า', area: 'Main Gate', vehicleType: 'รถยนต์', apiUser: '', apiPass: '', connectDate: '04/07/2026 10:17:41', monitor: 'ปกติ,ปกติ', status: '', deviceKey: '', defaultRate: '10000', prefix: '111', sapName: '', sapNameM: '', site: 'AroundD Lifestyle', checkCode: '' }
    ];
  }

  private calculateStats(): void {
    this.stats[0].value = this.providerSpots.length;
    this.stats[1].value = this.providerSpots.filter(s => s.monitor.includes('READY')).length;
    this.stats[2].value = this.providerSpots.filter(s => s.monitor.includes('ERROR')).length;
    this.stats[3].value = this.siteOptions.length;
  }

  // --- Filtering & Sorting ---
  public applyFilters(): void {
    let result = [...this.providerSpots];

    if (this.searchText) {
      const search = this.searchText.toLowerCase();
      result = result.filter(s =>
        s.name.toLowerCase().includes(search) ||
        s.ip.toLowerCase().includes(search) ||
        s.posId.toLowerCase().includes(search) ||
        s.site.toLowerCase().includes(search)
      );
    }

    if (this.filterSite) {
      result = result.filter(s => s.site === this.filterSite);
    }

    if (this.sortColumn) {
      result.sort((a: any, b: any) => {
        const valA = a[this.sortColumn];
        const valB = b[this.sortColumn];
        if (typeof valA === 'string') {
          return this.sortDirection === 'asc'
            ? valA.localeCompare(valB)
            : valB.localeCompare(valA);
        }
        return this.sortDirection === 'asc' ? valA - valB : valB - valA;
      });
    }

    this.totalItems = result.length;
    this.filteredSpots = result;
  }

  public sortData(column: string): void {
    if (this.sortColumn === column) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumn = column;
      this.sortDirection = 'asc';
    }
    this.applyFilters();
  }

  public toggleFilterDropdown(): void {
    this.isFilterDropdownOpen = !this.isFilterDropdownOpen;
  }

  public selectSiteFilter(site: string): void {
    this.filterSite = site;
    this.isFilterDropdownOpen = false;
    this.applyFilters();
    this.currentPage = 1;
  }

  // --- Pagination ---
  public get paginatedSpots(): ProviderSpot[] {
    const start = (this.currentPage - 1) * this.pageSize;
    return this.filteredSpots.slice(start, start + this.pageSize);
  }

  public get totalPages(): number {
    return Math.ceil(this.totalItems / this.pageSize) || 1;
  }

  public get pagesArray(): number[] {
    const pages = [];
    const maxVisible = 5;
    let start = Math.max(1, this.currentPage - 2);
    let end = Math.min(this.totalPages, start + maxVisible - 1);

    if (end - start + 1 < maxVisible) {
      start = Math.max(1, end - maxVisible + 1);
    }

    for (let i = start; i <= end; i++) pages.push(i);
    return pages;
  }

  public setPage(page: number): void {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
    }
  }

  public onPageSizeChange(event: any): void {
    this.pageSize = +event.target.value;
    this.currentPage = 1;
    this.applyFilters();
  }

  // --- Actions ---
  public onRefresh(): void {
    this.getProviderSpots();
  }

  public onEdit(spot: ProviderSpot): void {
    this.selectedSpot = { ...spot };
    this.isModalOpen = true;
  }

  public onCreate(): void {
    this.selectedSpot = null;
    this.isModalOpen = true;
  }

  public closeModal(): void {
    this.isModalOpen = false;
    this.selectedSpot = null;
  }
}
